import { useState, useRef, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import BtnNavigateBack from "@/components/ui/btn_navigate_back";
import { useShowNavigateBack } from "@/hooks/useShowNavigateBack";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { useLanguage } from "@/context/LanguageContext";
import { dealerPortalLabels } from "@/i18n";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useDealerPortal } from "@/hooks/useDealerPortal";
import { useMedia } from "@/hooks/useMedia";
import { useFile } from "@/hooks/useFile";
import { toast } from "sonner";
import type {
  DealerPortalVehicle,
  DealerPortalVehicleDocument,
  DealerPortalVehicleStatus,
} from "@/types/dealerPortal";
import { VEHICLE_STATUSES, VEHICLE_STATUS_LABELS } from "@/types/dealerPortal";
import { getDealerPortalVehicleStatusLabels } from "@/i18n";
import {
  FileText,
  Download,
  Upload,
  Trash2,
  Loader2,
  Car,
  ArrowLeft,
} from "lucide-react";
import { LicensePlate } from "@/components/my-auctions/LicensePlate";
import {
  getVehiclePdfActionCards,
  type VehiclePdfServiceType,
} from "@/components/dealer-portal/vehiclePdfCards";
import { DealerPortalServicePdfDialog } from "@/components/dealer-portal/DealerPortalServicePdfDialog";
import {
  DealerPortalVehicleImages,
  type DealerPortalImageCategory,
} from "@/components/dealer-portal/DealerPortalVehicleImages";
import {
  DealerPortalVehicleDocuments,
  type DealerPortalDocumentCategory,
} from "@/components/dealer-portal/DealerPortalVehicleDocuments";
import { DealerPortalVehicleComments } from "@/components/dealer-portal/DealerPortalVehicleComments";
import { DealerPortalVehicleCarDetails } from "@/components/dealer-portal/DealerPortalVehicleCarDetails";
import { NativeImage } from "@/components/native/NativeImage";

function VehicleCardImageThumb({
  mediaId,
  alt,
  className,
}: {
  mediaId: string;
  alt: string;
  className?: string;
}) {
  const { getDownloadUrlAsync } = useMedia();
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    getDownloadUrlAsync({ mediaId, expiresIn: 3600 })
      .then((url) => {
        if (!cancelled) setSrc(url);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [mediaId, getDownloadUrlAsync]);
  if (!src) {
    return (
      <div className={className}>
        <Car className="h-8 w-8 text-muted-foreground/50 mx-auto mt-4" />
      </div>
    );
  }
  return <NativeImage src={src} alt={alt} className={className} />;
}

export default function DealerPortalVehicleCardPage() {
  const { vehicleId } = useParams<{ vehicleId: string }>();
  const { language } = useLanguage();
  const d = dealerPortalLabels(language);
  const v = (d.vehicles ?? {}) as Record<string, string>;
  const showPageBack = useShowNavigateBack();
  const backLabel = v.back ?? "Tillbaka";

  const {
    vehicle,
    vehicleCarTests,
    updateVehicle,
    uploadVehicleDocument,
    deleteVehicleDocument,
    updateVehicleDocumentComment,
    addVehicleServiceDetail,
    updateVehicleServiceDetail,
    removeVehicleServiceDetail,
  } = useDealerPortal({
    vehicleId: vehicleId ?? null,
  });
  const { getDownloadUrlAsync } = useMedia();
  const { downloadFile } = useFile();

  const [headerParkingSpot, setHeaderParkingSpot] = useState("");
  const imageInputRef = useRef<HTMLInputElement>(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [deletingDocId, setDeletingDocId] = useState<string | null>(null);
  const [selectedPdfType, setSelectedPdfType] =
    useState<VehiclePdfServiceType | null>(null);

  const veh = vehicle.data;
  const isLoading = vehicle.isLoading;

  useEffect(() => {
    if (veh) setHeaderParkingSpot(veh.parkingSpot ?? "");
  }, [veh]);

  const handleStatusChange = (val: DealerPortalVehicleStatus) => {
    if (!veh || !val) return;
    updateVehicle.mutate(
      {
        id: veh.id,
        body: { status: val },
      },
      {
        onSuccess: () => toast.success(v.toastUpdated ?? "Fordon uppdaterat"),
        onError: (err: unknown) =>
          toast.error(
            (err as { message?: string })?.message ??
              v.toastUpdateError ??
              "Kunde inte uppdatera",
          ),
      },
    );
  };

  const handleSaveParkingSpot = () => {
    if (!veh) return;
    const trimmed = headerParkingSpot.trim() || null;
    if (trimmed === (veh.parkingSpot ?? null)) return;
    updateVehicle.mutate(
      {
        id: veh.id,
        body: { parkingSpot: trimmed },
      },
      {
        onSuccess: () => toast.success(v.toastUpdated ?? "Fordon uppdaterat"),
        onError: (err: unknown) =>
          toast.error(
            (err as { message?: string })?.message ??
              v.toastUpdateError ??
              "Kunde inte uppdatera",
          ),
      },
    );
  };

  const isUpdatingHeader = updateVehicle.isPending;
  const hasParkingChange =
    veh && (headerParkingSpot.trim() || null) !== (veh.parkingSpot ?? null);

  const handleUploadDocumentWithCategory = (
    file: File,
    documentCategory: DealerPortalDocumentCategory,
  ) => {
    if (!veh) return;
    setUploadingDoc(true);
    uploadVehicleDocument.mutate(
      {
        vehicleId: veh.id,
        file,
        documentCategory,
      },
      {
        onSuccess: () =>
          toast.success(v.documentUploaded ?? "Dokument uppladdat"),
        onError: (err: unknown) => {
          toast.error(
            (err as { message?: string })?.message ??
              v.documentUploadError ??
              "Kunde inte ladda upp dokument",
          );
        },
        onSettled: () => setUploadingDoc(false),
      },
    );
  };

  const handleUploadImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !veh) return;
    e.target.value = "";
    setUploadingDoc(true);
    uploadVehicleDocument.mutate(
      { vehicleId: veh.id, file },
      {
        onSuccess: () => toast.success(v.imageUploaded ?? "Bild uppladdat"),
        onError: (err: unknown) => {
          toast.error(
            (err as { message?: string })?.message ??
              v.documentUploadError ??
              "Kunde inte ladda upp bild",
          );
        },
        onSettled: () => setUploadingDoc(false),
      },
    );
  };

  const handleUploadImageWithCategory = (params: {
    vehicleId: string;
    file: File;
    imageCategory: DealerPortalImageCategory;
  }) => {
    setUploadingDoc(true);
    uploadVehicleDocument.mutate(
      {
        vehicleId: params.vehicleId,
        file: params.file,
        imageCategory: params.imageCategory,
      },
      {
        onSuccess: () => toast.success(v.imageUploaded ?? "Bild uppladdat"),
        onError: (err: unknown) => {
          toast.error(
            (err as { message?: string })?.message ??
              v.documentUploadError ??
              "Kunde inte ladda upp bild",
          );
        },
        onSettled: () => setUploadingDoc(false),
      },
    );
  };

  const handleDownloadDocument = async (doc: DealerPortalVehicleDocument) => {
    const firstMedia = doc.media?.[0];
    if (!firstMedia) return;
    try {
      const url = await getDownloadUrlAsync({
        mediaId: firstMedia.id,
        expiresIn: 3600,
      });
      await downloadFile(url, firstMedia.originalName ?? "document");
      toast.success(v.downloadStarted ?? "Nedladdning startad");
    } catch {
      toast.error(v.downloadFailed ?? "Kunde inte ladda ned");
    }
  };

  const handleDeleteDocument = (doc: DealerPortalVehicleDocument) => {
    const firstMedia = doc.media?.[0];
    if (!firstMedia || !veh) return;
    setDeletingDocId(doc.id);
    deleteVehicleDocument.mutate(
      { vehicleId: veh.id, mediaId: firstMedia.id },
      {
        onSuccess: () =>
          toast.success(v.documentRemoved ?? "Dokument borttaget"),
        onError: () =>
          toast.error(v.documentRemoveError ?? "Kunde inte ta bort dokument"),
        onSettled: () => setDeletingDocId(null),
      },
    );
  };

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto space-y-4 px-1 sm:px-0">
        {/* Header: Tillbaka + Reg.nr + Modell + Status + P-plats */}
        <div className="flex flex-col gap-3 sm:gap-4">
          {showPageBack ? (
            <BtnNavigateBack
              label={backLabel}
              to="/dealer-portal?tab=vehicles"
              className="w-fit mb-0 -ml-0.5"
            />
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className="w-fit gap-1.5 -ml-1"
              asChild
            >
              <Link to="/dealer-portal?tab=vehicles">
                <ArrowLeft className="h-4 w-4" />
                {backLabel}
              </Link>
            </Button>
          )}
          <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 sm:gap-4">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-wrap">
              <LicensePlate
                lpn={veh?.regNr ?? ""}
                className="text-lg font-semibold"
              />
              <span className="text-lg font-semibold truncate">
                {isLoading
                  ? "..."
                  : veh
                    ? `${veh.make} ${veh.model} (${veh.year})`
                    : ""}
              </span>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:ml-auto min-w-0">
              <div className="flex items-center gap-2">
                <Label className="text-muted-foreground text-sm whitespace-nowrap">
                  {v.statusLabel ?? "Status"}
                </Label>
                <Select
                  value={veh?.status ?? undefined}
                  onValueChange={(val) =>
                    handleStatusChange(val as DealerPortalVehicleStatus)
                  }
                  disabled={isUpdatingHeader}
                >
                  <SelectTrigger className="w-[140px]">
                    {isUpdatingHeader ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <SelectValue placeholder={v.statusLabel ?? "Status"} />
                    )}
                  </SelectTrigger>
                  <SelectContent>
                    {VEHICLE_STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {getDealerPortalVehicleStatusLabels(language)[s] ??
                          VEHICLE_STATUS_LABELS[s]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2 min-w-0 flex-1 sm:flex-initial">
                <Label className="text-muted-foreground text-sm whitespace-nowrap shrink-0">
                  {v.parkingSpot ?? "P-plats"}
                </Label>
                <div className="flex items-center gap-1.5 min-w-0 flex-1 sm:flex-initial">
                  <Input
                    value={headerParkingSpot}
                    onChange={(e) => setHeaderParkingSpot(e.target.value)}
                    disabled={isUpdatingHeader}
                    placeholder="Nr"
                    className="w-full min-w-0 max-w-[100px] sm:w-[100px]"
                  />
                  {hasParkingChange && (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={handleSaveParkingSpot}
                      disabled={isUpdatingHeader}
                      className="shrink-0"
                    >
                      {isUpdatingHeader ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        (v.save ?? "Spara ändring")
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {veh && (
          <>
            {/* PDF action cards */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {getVehiclePdfActionCards(language).map((card) => (
                <button
                  key={card.id}
                  type="button"
                  onClick={() => setSelectedPdfType(card.id)}
                  className={`p-4 rounded-xl border text-left transition-all duration-200 ${card.color}`}
                >
                  <div className="text-2xl mb-2">{card.icon}</div>
                  <h3 className="font-semibold text-foreground text-sm mb-1">
                    {card.title}
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    {card.description}
                  </p>
                </button>
              ))}
            </div>

            {/* Tabs: Info, Dokument, Bilder, Historik */}
            <Tabs defaultValue="info" className="w-full">
              <TabsList className="grid w-full grid-cols-4 h-9 p-1">
                <TabsTrigger value="info" className="text-xs sm:text-sm">
                  {v.tabInfo ?? "Info"}
                </TabsTrigger>
                <TabsTrigger value="documents" className="text-xs sm:text-sm">
                  {v.tabDocuments ?? "Dokument"}
                </TabsTrigger>
                <TabsTrigger value="images" className="text-xs sm:text-sm">
                  {v.tabImages ?? "Bilder"}
                </TabsTrigger>
                <TabsTrigger value="history" className="text-xs sm:text-sm">
                  {v.tabHistory ?? "Historik"}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="info" className="mt-4 space-y-4">
                {/* Kommentarer left, Fordonsdetaljer right */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-4">
                    {/* Kommentarer – bilhandlare kan lägga kommentarer och tagga kollegor */}
                    <DealerPortalVehicleComments vehicleId={veh.id} />
                  </div>
                  <div className="space-y-4">
                    <DealerPortalVehicleCarDetails
                      vehicle={veh}
                      onUpdated={() => void vehicle.refetch()}
                    />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="documents" className="mt-4">
                <DealerPortalVehicleDocuments
                  vehicleId={veh.id}
                  documents={veh.documents ?? []}
                  carTests={vehicleCarTests.data ?? []}
                  carTestsLoading={vehicleCarTests.isLoading}
                  onUpload={handleUploadDocumentWithCategory}
                  onDownload={handleDownloadDocument}
                  onDelete={handleDeleteDocument}
                  isUploading={uploadVehicleDocument.isPending}
                  deletingDocId={deletingDocId}
                />
              </TabsContent>

              <TabsContent value="images" className="mt-4">
                <DealerPortalVehicleImages
                  vehicleId={veh.id}
                  documents={veh.documents ?? []}
                  thumbnailLabel={v.thumbnailLabel ?? "Huvudbild"}
                  serviceDetails={veh.serviceDetails ?? []}
                  onAddServiceDetail={async (data) => {
                    try {
                      await addVehicleServiceDetail.mutateAsync({
                        vehicleId: veh.id,
                        data,
                      });
                      toast.success(
                        v.serviceDetailAdded ?? "Servicedetalj tillagd",
                      );
                    } catch {
                      toast.error(
                        v.serviceDetailAddErrorDescription ??
                          "Kunde inte lägga till servicedetalj",
                      );
                    }
                  }}
                  onRemoveServiceDetail={async (detailId) => {
                    try {
                      await removeVehicleServiceDetail.mutateAsync({
                        vehicleId: veh.id,
                        detailId,
                      });
                      toast.success(
                        v.serviceDetailRemoved ?? "Servicedetalj borttagen",
                      );
                    } catch {
                      toast.error(
                        v.serviceDetailRemoveFailedDesc ??
                          "Kunde inte ta bort servicedetalj",
                      );
                    }
                  }}
                  onUpdateServiceDetail={async (detailId, data) => {
                    try {
                      await updateVehicleServiceDetail.mutateAsync({
                        vehicleId: veh.id,
                        detailId,
                        data,
                      });
                      toast.success(
                        v.serviceDetailUpdated ?? "Servicedetalj uppdaterad",
                      );
                    } catch {
                      toast.error(
                        v.serviceDetailUpdateErrorDescription ??
                          "Kunde inte uppdatera servicedetalj",
                      );
                    }
                  }}
                  onUpdateDamageComment={async (documentId, comment) => {
                    updateVehicleDocumentComment.mutate(
                      { vehicleId: veh.id, documentId, comment },
                      {
                        onError: () =>
                          toast.error("Kunde inte uppdatera skadekommentar"),
                      },
                    );
                  }}
                  onUpload={handleUploadImageWithCategory}
                  onDelete={({ vehicleId, mediaId }) => {
                    setDeletingDocId(mediaId);
                    deleteVehicleDocument.mutate(
                      { vehicleId, mediaId },
                      {
                        onSuccess: () => {
                          toast.success(v.documentRemoved ?? "Bild borttagen");
                        },
                        onError: () =>
                          toast.error(
                            v.documentRemoveError ?? "Kunde inte ta bort bild",
                          ),
                        onSettled: () => setDeletingDocId(null),
                      },
                    );
                  }}
                  isUploading={uploadVehicleDocument.isPending}
                  deletingMediaId={deletingDocId}
                />
              </TabsContent>

              <TabsContent value="history" className="mt-4">
                <div className="space-y-4 bg-card rounded-lg p-6 shadow-sm">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-card-foreground">
                      {v.historyTitle ?? "Historik"}
                    </h3>
                  </div>
                  <div className="relative px-6">
                    <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-muted" />
                    <div className="space-y-6 py-4">
                      {veh.historyEvents && veh.historyEvents.length > 0 ? (
                        veh.historyEvents.map((ev) => {
                          const isJourneyRelated =
                            !!ev.journeyId ||
                            [
                              "LINKED_TO_JOURNEY",
                              "JOURNEY_STATUS_CHANGE",
                              "JOURNEY_UPDATED",
                              "JOURNEY_DELETED",
                            ].includes(ev.eventType);
                          return (
                            <div key={ev.id} className="relative pl-8">
                              <div
                                className={`absolute left-0 w-3 h-3 rounded-full -translate-x-[5px] ${
                                  isJourneyRelated
                                    ? "border-2 border-blue-500 bg-blue-50 dark:bg-blue-950/40"
                                    : "bg-primary/80"
                                }`}
                              />
                              <div
                                className={`rounded-lg p-3 ${
                                  isJourneyRelated
                                    ? "bg-blue-50/80 dark:bg-blue-950/30 border-l-2 border-blue-400"
                                    : "bg-muted/50"
                                }`}
                              >
                                <p className="font-medium text-foreground text-sm">
                                  {ev.description ?? ev.eventType}
                                </p>
                                <p className="text-xs text-muted-foreground mt-1">
                                  {ev.actor?.user?.username?.trim() || "System"}{" "}
                                  • {new Date(ev.createdAt).toLocaleString()}
                                </p>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <p className="text-sm text-muted-foreground pl-8">
                          {v.noHistoryYet ?? "Ingen historik än."}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </TabsContent>
            </Tabs>

            <DealerPortalServicePdfDialog
              open={!!selectedPdfType}
              onOpenChange={(o) => !o && setSelectedPdfType(null)}
              serviceType={selectedPdfType}
              vehicle={veh}
            />
          </>
        )}

        {!veh && !isLoading && (
          <p className="text-sm text-muted-foreground py-8">
            {v.vehicleLoadError ?? "Kunde inte ladda fordon."}
          </p>
        )}
      </div>
    </DashboardLayout>
  );
}
